import {
  Supertype,
  supertypeClass,
  property,
  remote,
  Remoteable,
  Persistable,
  amorphicStatic,
} from '@haventech/amorphic';
import {
  IsomorphicPersistent,
  Persistent,
  IsomorphicQuery,
  Query,
  Isomorphic,
  Serializable,
  AppController,
  SubController,
  Everything,
} from '@hit/amorphic-types';
import { Expense } from './Expense';
import { CashFlow } from './CashFlow';

@supertypeClass({ toClient: true, toServer: true })
export class ChildCare extends Expense {
  @property({ rule: ['currency'], validate: 'isWithin(0, 200000)' })
  youngCost: number;

  @property({ rule: ['currency'], validate: 'isWithin(0, 200000)' })
  oldCost: number;

  @property({ values: { '2': '2', '3': '3', '4': '4', '5': '5', '6': '6' } })
  youngAge: number;

  @property({ values: { '12': '12', '13': '13', '14': '14', '15': '15', '16': '16', '17': '17', '18': '18' } })
  oldAge: number;

  constructor() {
    super();
  }

  static new(customer) {
    return new ChildCare().init(customer);
  }

  init(customer) {
    super.init(customer, 'childcare', 0, 'Child Care');

    this.calculateCosts();
    return this;
  }

  isActive(deceased?) {
    return this.inCashFlow(deceased);
  }

  inCashFlow(deceased?) {
    return this.customer.capitalNeeds.earnedIncome[deceased === this.customer.primaryCustomer ? 1 : 0].needsChildCare;
  }

  resetCosts() {
    this.calculateCosts(true);
  }

  calculateCosts(force?) {
    if (force || !this.youngAge) {
      const costs = this.customer.assumptions.childCareCosts;
      const range = this.customer.assumptions.childCareCostStates[this.customer.profile.state];

      this.youngCost = costs[0].range[range];
      this.oldCost = costs[1].range[range];
      this.youngAge = costs[0].maxAge;
      this.oldAge = costs[1].maxAge;
    }
  }

  getYoungCost() {
    this.calculateCosts();

    return this.youngCost;
  }

  getOldCost() {
    this.calculateCosts();

    return this.oldCost;
  }

  getYoungAge() {
    this.calculateCosts();

    return this.youngAge;
  }

  getOldAge() {
    this.calculateCosts();

    return this.oldAge;
  }

  getStartingFlow(deceased) {
    const flow = <any>super.getStartingFlow(deceased);

    this.calculateCosts();
    flow.kids = [];

    for (let ix = 0; ix < this.customer.capitalNeeds.children.length; ++ix) {
      flow.kids[ix] = [];
      flow.kids[ix][0] = this.youngCost;
      flow.kids[ix][1] = this.oldCost;
    }

    return flow;
  }

  getAmount(flow, year, _deceased, yearOfDeath) {
    if (year < yearOfDeath) {
      return 0;
    }

    let total = 0;

    for (let ix = 0; ix < flow.kids.length; ++ix) {
      for (let jx = 0; jx < flow.kids[ix].length; ++jx) {
        const minAge = jx === 0 ? 0 : this.youngAge;
        const maxAge = jx === 0 ? this.youngAge : this.oldAge;

        if (
          year + this.customer.capitalNeeds.children[ix].age > minAge &&
          year + this.customer.capitalNeeds.children[ix].age <= maxAge
        ) {
          total -= Math.floor(flow.kids[ix][jx]);
        }
      }
    }

    return total;
  }

  adjustFlow(flow) {
    for (let ix = 0; ix < flow.kids.length; ++ix) {
      for (let jx = 0; jx < flow.kids[ix].length; ++jx) {
        flow.kids[ix][jx] += flow.kids[ix][jx] * this.customer.settings.inflation;
      }
    }
  }
}
